import { useState } from 'react'

import { api } from '../api/client'
import type { Integration, IntegrationCreated, Page } from '../api/types'
import { Card, Empty, ErrorNote, Spinner, TableScroll, formatDateTime } from '../components/ui'
import { useAction, useQuery } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'

const SOURCES = [
  'grafana',
  'prometheus',
  'zabbix',
  'nagios',
  'icinga',
  'victoriametrics',
  'kubernetes',
  'generic',
] as const

export function IntegrationsPage() {
  const { can } = useAuth()
  const integrations = useQuery(
    () => api.get<Page<Integration>>('/integrations', { limit: 200 }),
    [],
  )
  const action = useAction()

  const [form, setForm] = useState({
    name: '',
    source_type: 'prometheus' as (typeof SOURCES)[number],
    require_signature: true,
    labels: '',
  })
  const [created, setCreated] = useState<IntegrationCreated | null>(null)

  const writable = can('integrations.manage')

  const create = async () => {
    const defaultLabels = Object.fromEntries(
      form.labels
        .split(',')
        .map((pair) => pair.split('=').map((part) => part.trim()))
        .filter((pair) => pair.length === 2 && pair[0] && pair[1]),
    )
    const ok = await action.run(async () => {
      const result = await api.post<IntegrationCreated>('/integrations', {
        name: form.name,
        source_type: form.source_type,
        require_signature: form.require_signature,
        config: {},
        default_labels: defaultLabels,
      })
      setCreated(result)
    })
    if (ok) {
      setForm({ ...form, name: '', labels: '' })
      integrations.reload()
    }
  }

  const rotate = async (integration: Integration) => {
    await action.run(async () => {
      const result = await api.post<{ secret: string }>(
        `/integrations/${integration.id}/rotate-secret`,
      )
      setCreated({ ...integration, secret: result.secret, webhook_url: '' })
    })
  }

  const remove = async (integration: Integration) => {
    if (await action.run(() => api.del(`/integrations/${integration.id}`))) integrations.reload()
  }

  return (
    <>
      <ErrorNote>{integrations.error ?? action.error}</ErrorNote>

      {created && (
        <Card
          title="Секрет показывается один раз"
          actions={
            <button className="btn-ghost" onClick={() => setCreated(null)}>
              Скрыть
            </button>
          }
        >
          <div className="p-4 space-y-2 text-sm">
            {created.webhook_url && (
              <div>
                <div className="label">URL вебхука</div>
                <code className="block bg-ink-900 border border-ink-600 rounded p-2 font-mono text-xs break-all">
                  {created.webhook_url}
                </code>
              </div>
            )}
            <div>
              <div className="label">HMAC-секрет</div>
              <code className="block bg-ink-900 border border-ink-600 rounded p-2 font-mono text-xs break-all">
                {created.secret}
              </code>
            </div>
            <p className="text-xs text-slate-500">
              Подпись: <code>X-Signature: t=&lt;unix&gt;,v1=&lt;hmac_sha256(секрет,
              "&lt;t&gt;." + тело)&gt;</code>. Секрет в интерфейсе больше не появится — сохраните
              его в секрет-хранилище.
            </p>
          </div>
        </Card>
      )}

      <Card title="Источники алертов">
        {integrations.loading && !integrations.data ? (
          <Spinner />
        ) : (integrations.data?.items.length ?? 0) === 0 ? (
          <Empty>Интеграций нет — алертам неоткуда приходить.</Empty>
        ) : (
          <TableScroll>
            <thead>
              <tr>
                <th className="th">Название</th>
                <th className="th">Тип</th>
                <th className="th">Ключ</th>
                <th className="th">Подпись</th>
                <th className="th">Последнее событие</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody>
              {integrations.data!.items.map((integration) => (
                <tr key={integration.id} className={integration.is_active ? '' : 'opacity-50'}>
                  <td className="td">{integration.name}</td>
                  <td className="td text-slate-400">{integration.source_type}</td>
                  <td className="td font-mono text-xs text-slate-400">{integration.key}</td>
                  <td className="td text-xs">
                    {integration.require_signature ? (
                      <span className="text-emerald-400">обязательна</span>
                    ) : (
                      <span className="text-amber-400">выключена</span>
                    )}
                  </td>
                  <td className="td text-slate-400 tabular-nums">
                    {formatDateTime(integration.last_event_at)}
                  </td>
                  <td className="td text-right">
                    {writable && (
                      <div className="flex gap-1 justify-end">
                        <button className="btn-ghost text-xs" onClick={() => rotate(integration)}>
                          Сменить секрет
                        </button>
                        <button className="btn-danger text-xs" onClick={() => remove(integration)}>
                          Удалить
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableScroll>
        )}
      </Card>

      {writable && (
        <Card
          title="Новая интеграция"
          actions={
            <button className="btn-primary" disabled={action.pending || !form.name} onClick={create}>
              Создать
            </button>
          }
        >
          <div className="p-4 grid lg:grid-cols-4 gap-4">
            <div>
              <label className="label">Название</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Источник</label>
              <select
                className="input"
                value={form.source_type}
                onChange={(e) =>
                  setForm({ ...form, source_type: e.target.value as (typeof SOURCES)[number] })
                }
              >
                {SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Метки по умолчанию</label>
              <input
                className="input"
                placeholder="env=prod, dc=m9"
                value={form.labels}
                onChange={(e) => setForm({ ...form, labels: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm pb-2">
                <input
                  type="checkbox"
                  checked={form.require_signature}
                  onChange={(e) => setForm({ ...form, require_signature: e.target.checked })}
                />
                Требовать HMAC-подпись
              </label>
            </div>
          </div>
        </Card>
      )}
    </>
  )
}
